/**
 * Context preview component.
 *
 * Displays a preview of a context with its content and related questions.
 *
 * @module ContextPreview
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { X, FileText, Eye, Loader2 } from 'lucide-react';
import { contextTypeConfig } from './constants';
import { type Context, fetchContextBinaryContent } from '@/lib/contextApi';
import { getQuestionsByContextId } from '@/lib/questionApi';
import { LazyBinaryContent } from '@/components/admin/answersManager/LazyBinaryContent';
import { logger } from '@/lib/logger';

interface ContextPreviewProps {
  previewContext: Context;
  onClose: () => void;
  onSelect: (contextId: number) => void;
  onViewQuestion?: (questionId: string) => void;
}

export const ContextPreview = ({
  previewContext,
  onClose,
  onSelect,
  onViewQuestion,
}: ContextPreviewProps) => {
  const [relatedQuestions, setRelatedQuestions] = useState<any[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    const fetchRelatedQuestions = async () => {
      if (!previewContext?.id) {
        return;
      }

      setLoadingRelated(true);
      try {
        const questions = await getQuestionsByContextId(
          String(previewContext.id),
        );
        setRelatedQuestions(questions);
      } catch (error) {
        logger.error('Error fetching related questions:', error);
        setRelatedQuestions([]);
      } finally {
        setLoadingRelated(false);
      }
    };

    fetchRelatedQuestions();
  }, [previewContext?.id]);

  const renderContextContent = () => {
    if (previewContext.type === 'text') {
      return (
        <div className="prose max-w-none max-h-96 overflow-y-auto">
          <p className="font-ui whitespace-pre-wrap">
            {previewContext.content}
          </p>
        </div>
      );
    }

    if (previewContext.type === 'image' || previewContext.type === 'audio') {
      return (
        <div>
          {!!previewContext.id && (
            <LazyBinaryContent
              contextId={previewContext.id}
              mimeType={previewContext.mimeType}
              contextTitle={previewContext.name}
              contextType={previewContext.type}
              mediaUrl={previewContext.mediaUrl}
              initialBase64={previewContext.binaryContent}
              fetchBinaryContent={fetchContextBinaryContent}
            />
          )}

          {previewContext.type === 'audio' && previewContext.transcript && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="font-ui font-medium text-sm text-foreground">
                  Transcript:
                </span>
              </div>
              <div className="border rounded-lg p-3 bg-muted">
                <p className="font-ui text-sm whitespace-pre-wrap">
                  {previewContext.transcript}
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const getQuestionsDescription = () => {
    if (loadingRelated) {
      return 'Loading...';
    }
    if (relatedQuestions.length > 0) {
      const questionText = relatedQuestions.length === 1 ? 'question' : 'questions';
      return `${relatedQuestions.length} ${questionText} use this context`;
    }
    return 'No questions use this context';
  };

  const renderQuestionsContent = () => {
    if (loadingRelated) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      );
    }

    if (relatedQuestions.length > 0) {
      return (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {relatedQuestions.map((question) => (
            <div
              key={question.id}
              className="p-2 bg-background rounded border border-border hover:border-primary/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-ui text-xs text-foreground flex-1 line-clamp-2">
                  {question.text}
                </p>
                {onViewQuestion && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0 hover:bg-primary/10"
                    onClick={() => onViewQuestion(String(question.id))}
                    title="View question preview"
                  >
                    <Eye className="h-4 w-4 text-primary" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <p className="font-ui text-xs text-muted-foreground text-center py-2">
        No questions use this context
      </p>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg max-w-6xl w-[95vw] max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h3 className="font-ui text-lg font-semibold">Context Preview</h3>
          <Button type="button" variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-ui font-medium text-lg">{previewContext.name}</h4>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`font-ui text-xs px-2 py-1 rounded ${
                    contextTypeConfig[previewContext.type]?.badgeColor ||
                    'bg-muted text-muted-foreground'
                  }`}
                >
                  {previewContext.type}
                </span>
                {previewContext.fileExtension && (
                  <span className="font-ui text-xs text-muted-foreground">
                    .{previewContext.fileExtension}
                  </span>
                )}
              </div>
            </div>

            <div className="border rounded-lg p-4">{renderContextContent()}</div>

            <Card className="border-primary/20 bg-primary/5">
              <CardHeader className="pb-2">
                <CardTitle className="font-ui text-sm font-semibold flex items-center gap-1.5">
                  <FileText className="h-4 w-4" />
                  Questions Using This Context
                </CardTitle>
                <CardDescription className="font-ui text-xs">
                  {getQuestionsDescription()}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">{renderQuestionsContent()}</CardContent>
            </Card>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            <span className="font-ui">Close</span>
          </Button>
          <Button
            type="button"
            onClick={() => {
              onSelect(previewContext.id);
              onClose();
            }}
          >
            <span className="font-ui">Select This Context</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
